import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { isDepartmentScenario } from 'spw-components/worksheet/utils';
import {
  selectChildWorksheetSpacesRequestStatus,
  selectCurrentScenario,
  selectScenarios,
  selectTpsWorksheets,
  selectWorksheet,
  selectWorksheetRequestStatus,
} from 'spw-redux/modules/calculators/selectors';
import {
  currentProject as currentProjectSelector,
  selectProjectDepartments,
  projectSpaceTypes as projectSpaceTypesSelector,
  selectFirstBuilding,
} from 'spw-redux//modules/projects/selectors';
import { actions as projectActions } from 'spw-redux/modules/projects/actions';
import {
  actions as calculatorActions,
  getProjectScenarios,
} from 'spw-redux/modules/calculators/actions';
import { useParams } from 'react-router-v5';
import { useLocation } from 'react-router-dom-v5';
import { REQUEST_STATUS } from 'spw-constants';
import { setWorksheetDecimalsAreEnabled } from 'spw-redux/modules/worksheet/actions';
import {
  currentUser,
  selectMeasurementSystem,
} from 'spw-redux/modules/user/selectors';
import { setMeasurementSystem } from 'spw-redux/modules/user/actions';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';

export const useScenarioOrTotalProgramExportable = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useParams();
  const isTotalProgram = location.pathname.includes('total-programs');
  const currentScenario = useSelector(selectCurrentScenario);
  const currentProject = useSelector(currentProjectSelector);
  const scenarios = useSelector(selectScenarios);
  const projectSpaceTypes = useSelector(projectSpaceTypesSelector);
  const departments = useSelector(selectProjectDepartments);
  const currentBuilding = useSelector(selectFirstBuilding);

  const entityId = isTotalProgram
    ? (params as { totalProgramId: string }).totalProgramId
    : (params as { scenarioId: string }).scenarioId;

  useEffect(() => {
    if (isTotalProgram && !currentScenario?.id) {
      dispatch(calculatorActions.getTotalProgram(parseInt(entityId, 10)));
    } else if (!currentScenario?.id) {
      dispatch(calculatorActions.getScenario(parseInt(entityId, 10)));
    }
  }, [isTotalProgram, currentScenario?.id, entityId, dispatch]);

  useEffect(() => {
    if (currentScenario?.projectId && !currentProject) {
      dispatch(
        projectActions.getProjectInfoById(Number(currentScenario.projectId))
      );
    }
  }, [currentScenario?.projectId, currentProject, dispatch]);

  useEffect(() => {
    if (!scenarios.length && currentScenario?.id) {
      dispatch(getProjectScenarios(currentScenario.projectId));
    }
  }, [scenarios, currentScenario, dispatch]);

  useEffect(() => {
    if (
      currentProject &&
      isDepartmentScenario(currentScenario) &&
      !departments.length
    ) {
      dispatch(projectActions.getDepartmentsByProjectId(currentProject.id));
    }
  }, [currentProject, currentScenario, departments, dispatch]);

  return {
    isTotalProgram,
    currentScenario,
    currentBuilding,
    scenarios,
    currentProject,
    projectSpaceTypes,
    departments,
  };
};

export const useWorksheet = (isTotalProgram: boolean) => {
  const currentScenario = useSelector(selectCurrentScenario);
  const scenarios = useSelector(selectScenarios);
  const worksheet = useSelector(selectWorksheet);
  const projectSpaceTypes = useSelector(projectSpaceTypesSelector);
  const childWorksheetSpacesRequestStatus = useSelector(
    selectChildWorksheetSpacesRequestStatus
  );
  const worksheetRequestStatus = useSelector(selectWorksheetRequestStatus);

  const dispatch = useDispatch();

  useEffect(() => {
    // When the current scenario is a total program, the worksheet will be fetched
    // only if the project scenarios are available, the aggregated scenario has not been built and the current worksheet does not exist

    let shouldFetchWorksheet = Boolean(currentScenario);

    if (isTotalProgram) {
      shouldFetchWorksheet = shouldFetchWorksheet && scenarios.length > 0;
    }

    shouldFetchWorksheet = Boolean(
      shouldFetchWorksheet &&
        projectSpaceTypes.length &&
        !worksheet &&
        childWorksheetSpacesRequestStatus === REQUEST_STATUS.SUCCESS &&
        worksheetRequestStatus === REQUEST_STATUS.NONE
    );

    if (shouldFetchWorksheet) {
      dispatch(calculatorActions.fetchCurrentWorksheet());
    }
  }, [
    worksheetRequestStatus,
    isTotalProgram,
    currentScenario,
    scenarios,
    projectSpaceTypes,
    worksheet,
    childWorksheetSpacesRequestStatus,
    dispatch,
  ]);

  if (isTotalProgram) {
    const tpsWorksheets = useSelector(selectTpsWorksheets);

    return {
      worksheet,
      tpsWorksheets,
    };
  }

  return {
    worksheet,
  };
};

export const useVariantsOverride = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const urlParams = new URLSearchParams(location?.search);

  const showDecimals = urlParams.get('show_decimals') === 'true';
  const measurementSystemFromRequest = urlParams.get('measurement_system');
  const measurementSystemFromProfile = useSelector(selectMeasurementSystem);
  const user = useSelector(currentUser);

  useEffect(() => {
    dispatch(setWorksheetDecimalsAreEnabled(showDecimals));
  }, [dispatch, showDecimals]);

  useEffect(() => {
    const measurementSystemShouldBeOverwritten =
      measurementSystemFromRequest &&
      measurementSystemFromProfile !== measurementSystemFromRequest;
    if (measurementSystemShouldBeOverwritten) {
      dispatch(
        setMeasurementSystem(measurementSystemFromRequest as MeasurementSystem)
      );
    }
  }, [
    measurementSystemFromRequest,
    measurementSystemFromProfile,
    dispatch,
    user?.settings?.measurementSystemWeb,
  ]);

  return {
    showDecimals,
  };
};
